import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Loader2,
  ListVideo,
  Plus,
  GripVertical,
  Trash2,
  Clock,
  Image,
  Video,
  Code,
  Link,
  LayoutDashboard,
  Settings,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useSignagePlaylists, type Playlist, type PlaylistItem } from '@/hooks/signage';

interface ContentItem {
  id: string;
  name: string;
  content_type: string;
  storage_path: string | null;
  external_url: string | null;
  thumbnail_url: string | null;
  duration_seconds: number;
}

interface PlaylistBuilderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  playlistId: string;
  businessId: string;
  onSaved: () => void;
}

export function PlaylistBuilderDialog({
  open,
  onOpenChange,
  playlistId,
  businessId,
  onSaved,
}: PlaylistBuilderDialogProps) {
  const {
    getPlaylistWithItems,
    updatePlaylist,
    addItemToPlaylist,
    removeItemFromPlaylist,
    updateItemDuration,
    reorderPlaylistItems,
  } = useSignagePlaylists(businessId);

  const [playlist, setPlaylist] = useState<Playlist | null>(null);
  const [availableContent, setAvailableContent] = useState<ContentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showContentPicker, setShowContentPicker] = useState(false);

  // Settings state
  const [name, setName] = useState('');
  const [loopMode, setLoopMode] = useState<'continuous' | 'once' | 'shuffle'>('continuous');
  const [transitionType, setTransitionType] = useState<'fade' | 'slide' | 'zoom' | 'none'>('fade');
  const [transitionDuration, setTransitionDuration] = useState(500);

  // Load playlist and content
  useEffect(() => {
    if (open && playlistId) {
      loadData();
    }
  }, [open, playlistId]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load playlist with items
      const playlistData = await getPlaylistWithItems(playlistId);
      if (playlistData) {
        setPlaylist(playlistData);
        setName(playlistData.name);
        setLoopMode(playlistData.loop_mode);
        setTransitionType(playlistData.transition_type);
        setTransitionDuration(playlistData.transition_duration_ms);
      }

      // Load available content
      const { data: contentData } = await supabase
        .from('signage_content_items')
        .select('id, name, content_type, storage_path, external_url, thumbnail_url, duration_seconds')
        .eq('business_id', businessId)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      setAvailableContent(contentData || []);
    } catch (err) {
      console.error('Failed to load data:', err);
      toast.error('Failed to load playlist');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await updatePlaylist(playlistId, {
        name,
        loop_mode: loopMode,
        transition_type: transitionType,
        transition_duration_ms: transitionDuration,
      });
      setShowSettings(false);
    } finally {
      setSaving(false);
    }
  };

  const handleAddContent = async (contentId: string) => {
    const success = await addItemToPlaylist(playlistId, contentId);
    if (success) {
      await loadData();
    }
  };

  const handleRemoveItem = async (itemId: string) => {
    const success = await removeItemFromPlaylist(itemId);
    if (success) {
      await loadData();
    }
  };

  const handleDurationChange = async (itemId: string, duration: number | null) => {
    await updateItemDuration(itemId, duration);
    // Update local state
    if (playlist) {
      setPlaylist({
        ...playlist,
        items: playlist.items?.map(item =>
          item.id === itemId ? { ...item, duration_override: duration } : item
        ),
      });
    }
  };

  const handleMoveItem = async (index: number, direction: 'up' | 'down') => {
    if (!playlist?.items) return;

    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= playlist.items.length) return;

    const newItems = [...playlist.items];
    [newItems[index], newItems[newIndex]] = [newItems[newIndex], newItems[index]];

    // Update local state immediately for responsive UI
    setPlaylist({ ...playlist, items: newItems });

    // Save to database
    const itemIds = newItems.map(item => item.id);
    await reorderPlaylistItems(playlistId, itemIds);
  };

  const handleClose = () => {
    onSaved();
    onOpenChange(false);
  };

  const getContentIcon = (type: string) => {
    switch (type) {
      case 'image':
        return <Image className="w-4 h-4" />;
      case 'video':
        return <Video className="w-4 h-4" />;
      case 'html':
        return <Code className="w-4 h-4" />;
      case 'url':
        return <Link className="w-4 h-4" />;
      case 'dashboard':
        return <LayoutDashboard className="w-4 h-4" />;
      default:
        return <Image className="w-4 h-4" />;
    }
  };

  const getTotalDuration = () => {
    if (!playlist?.items) return 0;
    return playlist.items.reduce((sum, item) => {
      return sum + (item.duration_override || item.content_item.duration_seconds);
    }, 0);
  };

  const formatDuration = (seconds: number): string => {
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (secs === 0) return `${mins}m`;
    return `${mins}m ${secs}s`;
  };

  // Filter out content already in playlist
  const availableToAdd = availableContent.filter(
    content => !playlist?.items?.some(item => item.content_item_id === content.id)
  );

  if (loading) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-2xl">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ListVideo className="w-5 h-5" />
            {playlist?.name || 'Edit Playlist'}
          </DialogTitle>
          <DialogDescription>
            Add content and arrange the playback order
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col gap-4">
          {/* Stats bar */}
          <div className="flex items-center justify-between px-1">
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <span>{playlist?.items?.length || 0} items</span>
              <span>•</span>
              <span>{formatDuration(getTotalDuration())} total</span>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSettings(!showSettings)}
              >
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowContentPicker(!showContentPicker)}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Content
              </Button>
            </div>
          </div>

          {/* Settings panel */}
          {showSettings && (
            <div className="border rounded-lg p-4 space-y-4 bg-muted/30">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Playlist Name</Label>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Loop Mode</Label>
                  <Select value={loopMode} onValueChange={(v: any) => setLoopMode(v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="continuous">Continuous Loop</SelectItem>
                      <SelectItem value="once">Play Once</SelectItem>
                      <SelectItem value="shuffle">Shuffle</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Transition</Label>
                  <Select value={transitionType} onValueChange={(v: any) => setTransitionType(v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fade">Fade</SelectItem>
                      <SelectItem value="slide">Slide</SelectItem>
                      <SelectItem value="zoom">Zoom</SelectItem>
                      <SelectItem value="none">None</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Transition Duration (ms)</Label>
                  <Input
                    type="number"
                    min={0}
                    max={2000}
                    step={100}
                    value={transitionDuration}
                    onChange={(e) => setTransitionDuration(parseInt(e.target.value) || 500)}
                  />
                </div>
              </div>
              <div className="flex justify-end">
                <Button size="sm" onClick={handleSaveSettings} disabled={saving}>
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Save Settings
                </Button>
              </div>
            </div>
          )}

          {/* Content picker */}
          {showContentPicker && (
            <div className="border rounded-lg p-4 space-y-3 bg-muted/30">
              <Label>Available Content</Label>
              {availableToAdd.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  All content has been added to this playlist
                </p>
              ) : (
                <ScrollArea className="h-32">
                  <div className="space-y-2">
                    {availableToAdd.map((content) => (
                      <div
                        key={content.id}
                        className="flex items-center justify-between p-2 rounded-md border bg-background hover:bg-accent cursor-pointer"
                        onClick={() => handleAddContent(content.id)}
                      >
                        <div className="flex items-center gap-2">
                          {getContentIcon(content.content_type)}
                          <span className="text-sm">{content.name}</span>
                          <Badge className="text-xs">
                            {content.duration_seconds}s
                          </Badge>
                        </div>
                        <Plus className="w-4 h-4 text-muted-foreground" />
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </div>
          )}

          {/* Playlist items */}
          <ScrollArea className="flex-1 min-h-[200px]">
            {!playlist?.items?.length ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <ListVideo className="w-12 h-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No content in this playlist yet</p>
                <p className="text-sm text-muted-foreground">
                  Click "Add Content" above to get started
                </p>
              </div>
            ) : (
              <div className="space-y-2 pr-4">
                {playlist.items.map((item, index) => (
                  <div
                    key={item.id}
                    className="flex items-center gap-3 p-3 rounded-lg border bg-card"
                  >
                    {/* Drag handle / position */}
                    <div className="flex flex-col items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        disabled={index === 0}
                        onClick={() => handleMoveItem(index, 'up')}
                      >
                        <ChevronUp className="w-4 h-4" />
                      </Button>
                      <span className="text-xs text-muted-foreground font-mono w-6 text-center">
                        {index + 1}
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        disabled={index === playlist.items!.length - 1}
                        onClick={() => handleMoveItem(index, 'down')}
                      >
                        <ChevronDown className="w-4 h-4" />
                      </Button>
                    </div>

                    {/* Content info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        {getContentIcon(item.content_item.content_type)}
                        <span className="font-medium truncate">
                          {item.content_item.name}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                        <Badge variant="outline" className="text-xs capitalize">
                          {item.content_item.content_type}
                        </Badge>
                      </div>
                    </div>

                    {/* Duration */}
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-muted-foreground" />
                      <Input
                        type="number"
                        min={1}
                        max={300}
                        className="w-20 h-8 text-center"
                        value={item.duration_override || item.content_item.duration_seconds}
                        onChange={(e) => {
                          const value = parseInt(e.target.value);
                          if (value && value > 0) {
                            handleDurationChange(item.id, value);
                          }
                        }}
                      />
                      <span className="text-xs text-muted-foreground">sec</span>
                    </div>

                    {/* Remove button */}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      onClick={() => handleRemoveItem(item.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
